import React, { useState } from 'react';
import axios from 'axios';
import Api from '../../services/api';
import { clearBookingState } from '../../services/bookingStatePersistence';

const RatingModal = ({ booking, driverName, onRatingComplete, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear all booking cache from localStorage
  const clearBookingCache = () => {
    try {
      localStorage.removeItem('current_booking_data');
      localStorage.removeItem('trip_started_booking');
      clearBookingState();
      console.log('🗑️ Booking cache cleared from localStorage');
    } catch (error) {
      console.error('❌ Error clearing booking cache:', error);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('uttaapp_token');
      const bookingId = booking._id || booking.id;

      const response = await axios.post(
        Api.Rating.submitDriverRating,
        {
          bookingId: bookingId,
          rating: rating,
          feedback: feedback.trim() || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        console.log('✅ Rating submitted successfully');
        setLoading(false);
        // Clear booking cache after successful rating submission
        clearBookingCache();
        // Call onRatingComplete immediately to trigger navigation
        if (onRatingComplete) {
          onRatingComplete(response.data.rating);
        }
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      setError(error.response?.data?.message || 'Failed to submit rating. Please try again.');
      setLoading(false);
    }
  };

  const StarIcon = ({ filled, hovered, onClick, onMouseEnter, onMouseLeave }) => (
    <svg
      className={`w-10 h-10 cursor-pointer transition-all duration-200 ${
        filled || hovered
          ? 'text-yellow-400 fill-current transform scale-110'
          : 'text-gray-300 fill-none'
      }`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1}
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10010] p-4" style={{ display: 'flex' }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-slideUp">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-500 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">Rate Your Driver</h2>
            <button
              onClick={() => {
                // Clear booking cache when closing
                clearBookingCache();
                if (onClose) onClose();
              }}
              className="text-white hover:text-gray-200 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
            <p className="text-white text-sm mb-1">Driver</p>
            <p className="text-white text-xl font-bold">{driverName || 'Driver'}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
              <p className="text-red-700 text-sm font-semibold">{error}</p>
            </div>
          )}

          {/* Rating Stars */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-4 text-lg">
              How was your experience?
            </label>
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon
                  key={star}
                  filled={star <= rating}
                  hovered={star <= hoverRating}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                />
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center mt-4 text-gray-600 font-medium">
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </p>
            )}
          </div>

          {/* Feedback */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Share your feedback (optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us about your experience..."
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:ring-2 focus:ring-amber-200 transition-all resize-none"
              rows="4"
              disabled={loading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={() => {
                // Clear booking cache when skipping
                clearBookingCache();
                if (onClose) onClose();
              }}
              className="flex-1 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-lg transition-all duration-200"
              disabled={loading}
            >
              Skip
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || rating === 0}
              className={`flex-1 px-6 py-4 rounded-xl font-bold text-lg text-white transition-all transform ${
                rating === 0
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 hover:scale-105 shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Submitting...</span>
                </div>
              ) : (
                'Submit Rating'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingModal;

